<script setup lang="ts">
import { computed } from 'vue'
import { usePageStore } from '@voix/store/pageStore'
import { useLayoutStore } from '@voix/store/layoutStore'

const props = defineProps<{
  where: string
}>()

const localValue = defineModel()
const layoutStore = useLayoutStore()
const pageStore = usePageStore()

const layout = computed(() => {
  if (!layoutStore.layouts || !pageStore.currentPage)
    return null

  return layoutStore.layouts[pageStore.currentPage?.layout]
})

const showPalette = computed(() => {
  if (layout.value?.colorPalette)
    return layout.value?.colors[props.where]?.palette && Object.keys(layout.value?.colorPalette).length

  return false
})

function setColor(color: string) {
  if (isHexColor(color)) {
    localValue.value = color
    return
  }

  localValue.value = colorNameToHex(color)
}

function isHexColor(str: string) {
  const hexPattern = /^#?([a-f0-9]{3}|[a-f0-9]{4}|[a-f0-9]{6}|[a-f0-9]{8})$/i
  return hexPattern.test(str)
}

function colorNameToHex(color: any) {
  // Create a temporary DOM element
  const tempElement = document.createElement('div')
  // Set the element's color to the specified color name
  tempElement.style.color = color
  // Append the element to the document
  document.body.appendChild(tempElement)

  // Get the computed color value in RGB format
  const computedColor = window.getComputedStyle(tempElement).color

  // Remove the temporary element
  document.body.removeChild(tempElement)

  // Extract the RGB values from the computed color string
  const match = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  if (!match) {
    throw new Error('Invalid color name')
  }

  // Convert RGB values to hex
  const r = Number.parseInt(match[1]).toString(16).padStart(2, '0')
  const g = Number.parseInt(match[2]).toString(16).padStart(2, '0')
  const b = Number.parseInt(match[3]).toString(16).padStart(2, '0')

  return `#${r}${g}${b}`
}

const filteredColorPalette = computed(() => {
  if (!layout.value?.colorPalette)
    return {}

  return Object.entries(layout.value.colorPalette).reduce((acc, [color, rules]) => {
    if (rules[props.where]) {
      acc[color] = rules
    }

    return acc
  }, {})
})
</script>

<template>
  <ul v-if="showPalette && layout" class="flex flex-wrap">
    <li v-for="(colorRules, color) in filteredColorPalette" :key="color" class="mr-3 mt-3">
      <button class="w-5 h-5 rounded-full" :style="{ backgroundColor: color }" @click="setColor(color)" />
    </li>
  </ul>
</template>
